import type React from "react";
import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Drawer from "../Drawer/Drawer";
import { Button } from "../Button/Button";

export interface QASkippedJudge {
  judgeId: string;
  reason: string;
}

export interface QAActionResult {
  action: string;
  createdCount: number;
  skippedCount: number;
  createdScoresheetIds: string[];
  skipped: QASkippedJudge[];
}

export interface QAGenericActionResult {
  action: string;
  success: boolean;
  createdCount?: number;
  updatedCount?: number;
  assignedCount?: number;
  createdFlightsCount?: number;
  assignedEntriesCount?: number;
  skipped?: boolean;
  reason?: string;
}

export interface QAFloatingPanelProps {
  currentUser?: {
    isQa?: boolean;
  };
  context?: {
    contestEditionId?: string;
    flightId?: string;
    tableId?: string;
  };
  onGenerateMissingScoresheetsForFlight?: (payload: {
    contestsEditionId: string;
    flightId: string;
    scoreStrategy: "random_reasonable" | "deterministic";
  }) => Promise<QAActionResult>;
  onGenerateMissingScoresheetsForTable?: (payload: {
    contestsEditionId: string;
    tableId: string;
    scoreStrategy: "random_reasonable" | "deterministic";
  }) => Promise<QAActionResult>;
  onGenerateEntries?: (payload: {
    contestsEditionId: string;
    count: number;
  }) => Promise<QAGenericActionResult>;
  onPayEntries?: (payload: {
    contestsEditionId: string;
  }) => Promise<QAGenericActionResult>;
  onCheckInEntries?: (payload: {
    contestsEditionId: string;
  }) => Promise<QAGenericActionResult>;
  onRecodeEntries?: (payload: {
    contestsEditionId: string;
  }) => Promise<QAGenericActionResult>;
  onCreateFlights?: (payload: {
    contestsEditionId: string;
  }) => Promise<QAGenericActionResult>;
  onAssignTables?: (payload: {
    contestsEditionId: string;
  }) => Promise<QAGenericActionResult>;
}

export const QAFloatingPanel: React.FC<QAFloatingPanelProps> = ({
  currentUser,
  context,
  onGenerateMissingScoresheetsForFlight,
  onGenerateMissingScoresheetsForTable,
  onGenerateEntries,
  onPayEntries,
  onCheckInEntries,
  onRecodeEntries,
  onCreateFlights,
  onAssignTables,
}) => {
  const [t] = useTranslation();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<QAActionResult | QAGenericActionResult | null>(null);
  const [scoreStrategy, setScoreStrategy] = useState<"random_reasonable" | "deterministic">("deterministic");
  const [generateCount, setGenerateCount] = useState<number>(10);

  // Derive contestEditionId fallback from pathname if not explicitly passed in props
  const parsedContestEditionId =
    context?.contestEditionId ||
    window.location.pathname.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    )?.[0];

  if (!currentUser?.isQa) {
    return null;
  }

  const handleGenerateForFlight = async () => {
    if (!parsedContestEditionId || !context?.flightId || !onGenerateMissingScoresheetsForFlight) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onGenerateMissingScoresheetsForFlight({
        contestsEditionId: parsedContestEditionId,
        flightId: context.flightId,
        scoreStrategy,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateForTable = async () => {
    if (!parsedContestEditionId || !context?.tableId || !onGenerateMissingScoresheetsForTable) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onGenerateMissingScoresheetsForTable({
        contestsEditionId: parsedContestEditionId,
        tableId: context.tableId,
        scoreStrategy,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateEntries = async () => {
    if (!parsedContestEditionId || !onGenerateEntries) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onGenerateEntries({
        contestsEditionId: parsedContestEditionId,
        count: generateCount,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handlePayEntries = async () => {
    if (!parsedContestEditionId || !onPayEntries) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onPayEntries({ contestsEditionId: parsedContestEditionId });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInEntries = async () => {
    if (!parsedContestEditionId || !onCheckInEntries) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onCheckInEntries({ contestsEditionId: parsedContestEditionId });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleRecodeEntries = async () => {
    if (!parsedContestEditionId || !onRecodeEntries) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onRecodeEntries({ contestsEditionId: parsedContestEditionId });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlights = async () => {
    if (!parsedContestEditionId || !onCreateFlights) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onCreateFlights({ contestsEditionId: parsedContestEditionId });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTables = async () => {
    if (!parsedContestEditionId || !onAssignTables) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await onAssignTables({ contestsEditionId: parsedContestEditionId });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.errors?.[0]?.detail || err?.message || "Failed to execute QA action");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button (using custom Button wrapper as mandated) */}
      <Button
        id="qa-panel-trigger"
        onClick={() => setOpened(true)}
        className={classNames(
          "!fixed !bottom-6 !right-6 !z-[100] !h-12 !w-12 !rounded-full p-0 flex items-center justify-center text-white shadow-lg",
          "bg-amber-600 hover:bg-amber-700 active:scale-95",
          "dark:bg-amber-500 dark:hover:bg-amber-600",
          "transition-all duration-200"
        )}
        title={t("global:shared.qaToolkit.title", "QA Actions Toolkit")}
      >
        <div className="icon-[ph--flask] h-6 w-6" />
      </Button>

      {/* Toolkit Drawer */}
      <Drawer
        id="qa-panel-drawer"
        opened={opened}
        onClose={() => {
          setOpened(false);
          setError(null);
          setResult(null);
        }}
        title={t("global:shared.qaToolkit.title", "QA Actions Toolkit")}
        subtitle={t("global:shared.qaToolkit.subtitle", "Developer & QA manual testing tools")}
        width={380}
      >
        <div className="flex flex-col space-y-6 font-sans">
          {/* Strategy Select */}
          <div className="flex flex-col space-y-2">
            <label className="text-xs font-semibold text-content-500 dark:text-content-400 uppercase tracking-wider">
              {t("global:shared.qaToolkit.scoreStrategyLabel", "Score Generation Strategy")}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={scoreStrategy === "deterministic" ? "filled" : "outlined"}
                onClick={() => setScoreStrategy("deterministic")}
                className="!text-xs py-2 w-full"
              >
                {t("global:shared.qaToolkit.strategyDeterministic", "Deterministic (70%)")}
              </Button>
              <Button
                variant={scoreStrategy === "random_reasonable" ? "filled" : "outlined"}
                onClick={() => setScoreStrategy("random_reasonable")}
                className="!text-xs py-2 w-full"
              >
                {t("global:shared.qaToolkit.strategyRandom", "Random (60%-85%)")}
              </Button>
            </div>
          </div>

          {/* Context Details */}
          <div className="rounded-xl bg-base-50/70 border border-solid border-content-100 p-4 dark:bg-content-900/30 dark:border-content-800/60">
            <h3 className="text-xs font-semibold text-content-600 dark:text-content-400 uppercase tracking-wider mb-2">
              {t("global:shared.qaToolkit.activeContext", "Active Context")}
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-content-400">{t("global:shared.qaToolkit.contestEdition", "Edition:")}</span>
                <span className="font-mono text-content-700 dark:text-content-300 select-all">
                  {parsedContestEditionId || t("global:shared.qaToolkit.none", "None")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-content-400">{t("global:shared.qaToolkit.flight", "Flight:")}</span>
                <span className="font-mono text-content-700 dark:text-content-300 select-all">
                  {context?.flightId || t("global:shared.qaToolkit.none", "None")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-content-400">{t("global:shared.qaToolkit.table", "Table:")}</span>
                <span className="font-mono text-content-700 dark:text-content-300 select-all">
                  {context?.tableId || t("global:shared.qaToolkit.none", "None")}
                </span>
              </div>
            </div>
          </div>

          {/* Action Triggers */}
          <div className="flex flex-col space-y-4 max-h-[50vh] overflow-y-auto pr-1">
            <h3 className="text-xs font-semibold text-content-500 dark:text-content-400 uppercase tracking-wider">
              {t("global:shared.qaToolkit.availableActions", "Actions")}
            </h3>

            {/* Step 1: Generate Entries */}
            <div className="flex flex-col space-y-2 border border-solid border-content-100 dark:border-content-800/60 rounded-xl p-3 bg-base-50/20">
              <span className="text-xs font-semibold text-content-600 dark:text-content-400">
                {t("global:shared.qaToolkit.step1", "Phase 1: Entry Registration")}
              </span>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={generateCount}
                  onChange={(e) => setGenerateCount(parseInt(e.target.value) || 1)}
                  className="w-16 px-2 py-1.5 text-xs rounded border border-solid border-content-300 dark:border-content-700 bg-white dark:bg-content-900 text-content-800 dark:text-content-100"
                />
                <Button
                  id="qa-btn-generate-entries"
                  type="primary"
                  onClick={handleGenerateEntries}
                  disabled={loading || !parsedContestEditionId || !onGenerateEntries}
                  className="flex-1 text-xs py-1.5"
                  startIcon={<div className="icon-[ph--plus-circle] h-4 w-4" />}
                >
                  {t("global:shared.qaToolkit.generateEntries", "Generate Entries")}
                </Button>
              </div>
              <Button
                id="qa-btn-pay-entries"
                type="secondary"
                variant="outlined"
                onClick={handlePayEntries}
                disabled={loading || !parsedContestEditionId || !onPayEntries}
                className="w-full text-xs py-1.5"
                startIcon={<div className="icon-[ph--credit-card] h-4 w-4" />}
              >
                {t("global:shared.qaToolkit.payEntries", "Mark All Unpaid as Paid")}
              </Button>
            </div>

            {/* Step 2: Check-in & Recoding */}
            <div className="flex flex-col space-y-2 border border-solid border-content-100 dark:border-content-800/60 rounded-xl p-3 bg-base-50/20">
              <span className="text-xs font-semibold text-content-600 dark:text-content-400">
                {t("global:shared.qaToolkit.step2", "Phase 2: Check-in & Recode")}
              </span>
              <Button
                id="qa-btn-check-in-entries"
                type="primary"
                onClick={handleCheckInEntries}
                disabled={loading || !parsedContestEditionId || !onCheckInEntries}
                className="w-full text-xs py-1.5"
                startIcon={<div className="icon-[ph--package] h-4 w-4" />}
              >
                {t("global:shared.qaToolkit.checkInEntries", "Check In All Entries")}
              </Button>
              <Button
                id="qa-btn-recode-entries"
                type="secondary"
                variant="outlined"
                onClick={handleRecodeEntries}
                disabled={loading || !parsedContestEditionId || !onRecodeEntries}
                className="w-full text-xs py-1.5"
                startIcon={<div className="icon-[ph--qr-code] h-4 w-4" />}
              >
                {t("global:shared.qaToolkit.recodeEntries", "Recode All Checked-In")}
              </Button>
            </div>

            {/* Step 3: Flight & Table Assignments */}
            <div className="flex flex-col space-y-2 border border-solid border-content-100 dark:border-content-800/60 rounded-xl p-3 bg-base-50/20">
              <span className="text-xs font-semibold text-content-600 dark:text-content-400">
                {t("global:shared.qaToolkit.step3", "Phase 3: Flights & Tables")}
              </span>
              <Button
                id="qa-btn-create-flights"
                type="primary"
                onClick={handleCreateFlights}
                disabled={loading || !parsedContestEditionId || !onCreateFlights}
                className="w-full text-xs py-1.5"
                startIcon={<div className="icon-[ph--intersect] h-4 w-4" />}
              >
                {t("global:shared.qaToolkit.createFlights", "Create Flights")}
              </Button>
              <Button
                id="qa-btn-assign-tables"
                type="secondary"
                variant="outlined"
                onClick={handleAssignTables}
                disabled={loading || !parsedContestEditionId || !onAssignTables}
                className="w-full text-xs py-1.5"
                startIcon={<div className="icon-[ph--circles-three-plus] h-4 w-4" />}
              >
                {t("global:shared.qaToolkit.assignTables", "Assign Tables")}
              </Button>
            </div>

            {/* Step 4: Judging Scoresheets Generation */}
            <div className="flex flex-col space-y-2 border border-solid border-content-100 dark:border-content-800/60 rounded-xl p-3 bg-base-50/20">
              <span className="text-xs font-semibold text-content-600 dark:text-content-400">
                {t("global:shared.qaToolkit.step4", "Phase 4: Scoresheet Completion")}
              </span>

              {/* Generate for Flight */}
              <div className="flex flex-col space-y-1">
                <Button
                  id="qa-btn-generate-flight"
                  type="primary"
                  onClick={handleGenerateForFlight}
                  disabled={loading || !parsedContestEditionId || !context?.flightId || !onGenerateMissingScoresheetsForFlight}
                  className="w-full text-xs py-1.5"
                  startIcon={<div className="icon-[ph--beer-bottle] h-4 w-4" />}
                >
                  {t("global:shared.qaToolkit.generateForFlight", "Fill Flight Scoresheets")}
                </Button>
                <span className="text-[9px] text-content-400 leading-normal pl-1">
                  {t(
                    "global:shared.qaToolkit.generateForFlightHelp",
                    "Creates missing scoresheets for all other judges in the active flight."
                  )}
                </span>
              </div>

              {/* Generate for Table */}
              <div className="flex flex-col space-y-1 mt-1">
                <Button
                  id="qa-btn-generate-table"
                  type="secondary"
                  variant="outlined"
                  onClick={handleGenerateForTable}
                  disabled={loading || !parsedContestEditionId || !context?.tableId || !onGenerateMissingScoresheetsForTable}
                  className="w-full text-xs py-1.5"
                  startIcon={<div className="icon-[ph--users] h-4 w-4" />}
                >
                  {t("global:shared.qaToolkit.generateForTable", "Fill Table Scoresheets")}
                </Button>
                <span className="text-[9px] text-content-400 leading-normal pl-1">
                  {t(
                    "global:shared.qaToolkit.generateForTableHelp",
                    "Creates missing scoresheets for all entries/judges at this table."
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Action Loading Status */}
          {loading && (
            <div className="flex items-center justify-center space-x-3 text-sm text-content-600 dark:text-content-400 bg-base-50 dark:bg-content-800/40 p-4 rounded-xl border border-solid border-content-100 dark:border-content-800/60">
              <div className="icon-[ph--circle-notch] animate-spin h-5 w-5 text-amber-500" />
              <span>{t("global:shared.qaToolkit.loading", "Executing QA action...")}</span>
            </div>
          )}

          {/* Action Error Output */}
          {error && (
            <div className="flex flex-col space-y-1 p-4 rounded-xl border border-solid border-error-200 bg-error-50 dark:bg-error-950/20 dark:border-error-800/50">
              <h4 className="text-xs font-semibold text-error-700 dark:text-error-400 flex items-center">
                <div className="icon-[ph--warning-octagon-fill] mr-1.5 h-4 w-4" />
                {t("global:shared.qaToolkit.errorTitle", "Action Failed")}
              </h4>
              <p className="text-xs text-error-600 dark:text-error-400 break-words leading-relaxed">{error}</p>
            </div>
          )}

          {/* Action Success Result */}
          {result && (
            <div className="flex flex-col space-y-3 p-4 rounded-xl border border-solid border-accent-200 bg-accent-50 dark:bg-accent-950/20 dark:border-accent-800/50 font-sans">
              <h4 className="text-xs font-semibold text-accent-700 dark:text-accent-400 flex items-center">
                <div className="icon-[ph--check-circle-fill] mr-1.5 h-4 w-4" />
                {t("global:shared.qaToolkit.successTitle", "Action Completed")}
              </h4>
              <div className="space-y-1.5 text-xs">
                {result.action === "generate_missing_scoresheets_for_flight" ||
                result.action === "generate_missing_scoresheets_for_table" ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-content-500">{t("global:shared.qaToolkit.created", "Created:")}</span>
                      <span className="font-semibold text-content-800 dark:text-content-200">
                        {(result as QAActionResult).createdCount} {t("global:shared.qaToolkit.scoresheets", "scoresheets")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-content-500">{t("global:shared.qaToolkit.skipped", "Skipped:")}</span>
                      <span className="font-semibold text-content-800 dark:text-content-200">
                        {(result as QAActionResult).skippedCount} {t("global:shared.qaToolkit.scoresheets", "scoresheets")}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    {"createdCount" in result && result.createdCount !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-content-500">{t("global:shared.qaToolkit.created", "Created:")}</span>
                        <span className="font-semibold text-content-800 dark:text-content-200">
                          {result.createdCount} {t("global:shared.qaToolkit.entries", "entries")}
                        </span>
                      </div>
                    )}
                    {"updatedCount" in result && result.updatedCount !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-content-500">{t("global:shared.qaToolkit.updated", "Updated:")}</span>
                        <span className="font-semibold text-content-800 dark:text-content-200">
                          {result.updatedCount} {t("global:shared.qaToolkit.entries", "entries")}
                        </span>
                      </div>
                    )}
                    {"assignedCount" in result && result.assignedCount !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-content-500">{t("global:shared.qaToolkit.assigned", "Assigned:")}</span>
                        <span className="font-semibold text-content-800 dark:text-content-200">
                          {result.assignedCount} {t("global:shared.qaToolkit.records", "records")}
                        </span>
                      </div>
                    )}
                    {"createdFlightsCount" in result && result.createdFlightsCount !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-content-500">{t("global:shared.qaToolkit.createdFlights", "Created Flights:")}</span>
                        <span className="font-semibold text-content-800 dark:text-content-200">
                          {result.createdFlightsCount} {t("global:shared.qaToolkit.flights", "flights")}
                        </span>
                      </div>
                    )}
                    {"assignedEntriesCount" in result && result.assignedEntriesCount !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-content-500">{t("global:shared.qaToolkit.assignedEntries", "Assigned Entries:")}</span>
                        <span className="font-semibold text-content-800 dark:text-content-200">
                          {result.assignedEntriesCount} {t("global:shared.qaToolkit.entries", "entries")}
                        </span>
                      </div>
                    )}
                    {"skipped" in result && result.skipped && (
                      <div className="text-[10px] text-content-500 italic mt-1">
                        {t("global:shared.qaToolkit.skippedReason", "Skipped: {{reason}}", { reason: result.reason })}
                      </div>
                    )}
                  </>
                )}
              </div>

              {"skipped" in result && Array.isArray(result.skipped) && result.skipped.length > 0 && (
                <div className="flex flex-col space-y-1.5 pt-2 border-t border-solid border-accent-200/50 dark:border-accent-800/40">
                  <span className="text-[10px] font-semibold text-content-500 uppercase tracking-wider">
                    {t("global:shared.qaToolkit.skippedJudges", "Skipped Judges details")}
                  </span>
                  <div className="max-h-24 overflow-y-auto space-y-1">
                    {(result.skipped as QASkippedJudge[]).map((skip, idx) => (
                      <div key={idx} className="flex justify-between text-[10px] text-content-500">
                        <span className="font-mono">{skip.judgeId.slice(0, 8)}...</span>
                        <span>
                          {skip.reason === "already_exists"
                            ? t("global:shared.qaToolkit.skipReasonAlreadyExists", "already exists")
                            : skip.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};
